/**
 * superpatronen-search.js
 *
 * Normalisiert Such-Queries für SUPERPATRONEN — zwei getrennte Pfade.
 *
 * Architektur:
 *   1. Predictive Search (Dropdown beim Tippen):
 *      Fängt /search/suggest komplett ab, feuert ZWEI parallele Calls
 *      mit jeweils eigener Normalisierung und eigener Liquid-Section,
 *      baut eine synthetische Response zusammen, die wie das Original
 *      predictive-search.liquid-Markup aussieht.
 *
 *      - Linke Spalte: 'predictive-search-printers' Section
 *        Resources: queries, collections, pages, articles
 *        Normalisierung: 'printer' (alles splitten, kein Suffix-Schutz)
 *
 *      - Rechte Spalte: 'predictive-search-products' Section
 *        Resources: products (sucht via title + tag, Drucker-Modelle
 *        stehen in den Tags der Patronen)
 *        Normalisierung: 'cartridge' (Zahl-Buchstabe-Split mit
 *        Cartridge-Suffix-Schutz, damit '207X' nicht zerstört wird)
 *
 *   2. Form-Submit (Enter im Suchfeld) → Result-Seite /search?q=…
 *      Eine Normalisierung, default 'cartridge'.
 *
 *   3. Direct-Link auf /search?q=… → einmaliger Redirect, default 'cartridge'.
 *
 * Die Result-Seite (/search?q=…) ist nicht angefasst.
 *
 * ──────────────────────────────────────────────────────────────────────
 * MODI in normalize(query, mode):
 *
 *   'cartridge' (default, für Patronen-Suche & Form-Submit):
 *     Zahl-Buchstabe-Split mit Cartridge-Suffix-Schutz.
 *     'HP 207X' bleibt 'HP 207X', '305A' bleibt '305A', '924E' bleibt.
 *     'm479dw' wird zu 'm 479 dw' (dw kein Suffix).
 *
 *   'printer' (für Drucker-Suche):
 *     Zahl-Buchstabe-Split immer aktiv.
 *     'M404x' wird zu 'M 404 x', '207X' wird zu '207 X'.
 *
 * Beide Modi machen identisch: Buchstabe-Zahl-Split, Marken-Split
 * (aggressive), Serien-Split (boundary).
 *
 * MATCH-MODI in insertSpacesAround:
 *
 *   'aggressive' (für Marken):
 *     Splittet vor und nach dem Token bei jedem Nicht-Whitespace.
 *
 *   'boundary' (für Serien):
 *     Splittet nur nach dem Token, und nur wenn vor dem Token bereits
 *     eine Wortgrenze steht. Verhindert, dass 'xpress' in 'express'
 *     fälschlich erkannt wird.
 * ──────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ═══════════════════════════════════════════════════════════════════
  // DATEN
  // ═══════════════════════════════════════════════════════════════════

  const BRANDS = [
    'Brother', 'Canon', 'Dell', 'Develop', 'Epson', 'G&G', 'HP', 'IBM',
    'Kodak', 'Konica', 'Kyocera', 'Lexmark', 'OKI', 'Olivetti', 'Panasonic',
    'Philips', 'Ricoh', 'Samsung', 'Sharp', 'Tally', 'Toshiba', 'Utax', 'Xerox'
  ];

  const SERIES = [
    'LaserJet', 'DeskJet', 'Pixma', 'i-SENSYS', 'OfficeJet', 'PhotoSmart',
    'imageRUNNER', 'WorkForce', 'EcoTank', 'Aficio', 'P-Touch', 'ECOSYS',
    'Stylus', 'PSC', 'DesignJet', 'WorkCentre', 'Phaser', 'imagePROGRAF',
    'Minolta', 'MFC-J', 'Xpress', 'Microline', 'SureColor', 'HL-L',
    'Optra', 'Ineo', 'VersaLink', 'TASKalfa', 'MFC-L', 'Aculaser',
    'iR-C', 'Infoprint', 'ProXpress', 'PageWide', 'Maxify', 'MX-M',
    'DCP-L', 'KX-MB', 'MultiXpress', 'iR-ADV', 'DCP-J', 'BJC',
    'TM-U', 'Selphy', 'Intellifax', 'SL-M', 'Multipass', 'KX-FLB',
    'Genicom', 'FS-C', 'MX-B', 'Lasershot', 'D-Color', 'ECR',
    'KX-FL', 'PLQ', 'P-C', 'AltaLink'
  ];

  // Patronen-Suffixe — nur relevant in mode='cartridge'.
  const CARTRIDGE_SUFFIXES = [
    'XL', 'XXL', 'XXXL', 'HC',
    'A',  // HP Standard (122A, 305A, 410A, 504A, 826A, …)
    'X',  // HP High-Yield (201X, 207X, 410X, 508X, 656X, 973X, …)
    'H',  // Canon / Lexmark (045H, 067H, 702H, 802H)
    'L',  // Epson / Canon (408L, T10L)
    'S',  // Lexmark (802S)
    'Z',  // HP (217Z)
    'E'   // HP (924E)
  ];

  const SORTED_BRANDS = [...BRANDS].sort((a, b) => b.length - a.length);
  const SORTED_SERIES = [...SERIES].sort((a, b) => b.length - a.length);

  // ═══════════════════════════════════════════════════════════════════
  // NORMALIZE
  // ═══════════════════════════════════════════════════════════════════

  function normalize(query, mode) {
    if (!query || typeof query !== 'string') return query;
    let q = query.trim();
    if (!q) return query;
    mode = mode || 'cartridge';

    // Regel A (Teil 1): Buchstabe→Zahl trennen.
    //   'ts8550' → 'ts 8550'
    q = q.replace(/([a-zA-Z])(\d)/g, '$1 $2');

    // Regel A (Teil 2): Zahl→Buchstabe trennen.
    //   mode='cartridge': Suffix-Schutz aktiv ('207x' bleibt)
    //   mode='printer':   immer splitten ('207x' wird zu '207 x')
    q = q.replace(/(\d+)([a-zA-Z]+)/g, (match, digits, letters) => {
      if (mode === 'cartridge' && CARTRIDGE_SUFFIXES.includes(letters.toUpperCase())) {
        return match;
      }
      return digits + ' ' + letters;
    });

    // Regel B: Marken aus zusammengeschriebenen Wörtern lösen.
    q = insertSpacesAround(q, SORTED_BRANDS, 'aggressive');

    // Regel C: Serien aus zusammengeschriebenen Wörtern lösen.
    q = insertSpacesAround(q, SORTED_SERIES, 'boundary');

    q = q.replace(/\s+/g, ' ').trim();
    return q;
  }

  function insertSpacesAround(query, tokens, mode) {
    let q = query;
    for (const token of tokens) {
      const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      if (mode === 'aggressive') {
        q = q.replace(new RegExp(`(\\S)(${escaped})`, 'gi'), '$1 $2');
        q = q.replace(new RegExp(`(${escaped})(\\S)`, 'gi'), '$1 $2');
      } else if (mode === 'boundary') {
        // Vor Token muss (^|\s|\d) stehen, nach Token Nicht-Whitespace.
        // Verhindert Substring-Matches wie 'xpress' in 'express'.
        q = q.replace(
          new RegExp(`(^|\\s|\\d)(${escaped})(\\S)`, 'gi'),
          '$1$2 $3'
        );
      }
    }
    return q;
  }

  // ═══════════════════════════════════════════════════════════════════
  // INTEGRATION 1: Fetch-Intercept mit Two-Call-Architektur
  // ═══════════════════════════════════════════════════════════════════

  // Hält den AbortController des aktuell laufenden Predictive-Search-Calls.
  // Bei einem neuen Call wird der alte abgebrochen, damit stale Antworten
  // nicht über aktuelle Ergebnisse drüberrendern.
  let activePredictiveSearchController = null;

  const originalFetch = window.fetch;

  /**
   * Entscheidet, ob ein fetch-Call abgefangen wird.
   *
   * Abgefangen wird ausschliesslich der Section-Rendering-Pfad
   * /search/suggest — auch mit Locale-Praefix, z. B. /de/search/suggest.
   * Das ist exakt der Pfad, den routes.predictive_search_url in
   * predictive-search.js liefert (Hauptsuche im Header).
   *
   * NICHT abgefangen wird /search/suggest.json. Dieser Ajax-Endpoint
   * liefert JSON und wird von anderen Bausteinen genutzt, z. B. dem
   * Druckermodell-Finder in print-selector.liquid. Die Two-Call-
   * Architektur antwortet mit HTML — ein JSON-Consumer scheitert daran
   * mit einem Parse-Fehler.
   */
  function isPredictiveSearchSectionUrl(url) {
    if (!url || typeof url !== 'string') return false;
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      return /(^|\/)search\/suggest$/.test(pathname);
    } catch (e) {
      return false;
    }
  }

  window.fetch = function (input, options) {
    try {
      const url = (typeof input === 'string') ? input : (input && input.url);
      if (isPredictiveSearchSectionUrl(url)) {
        // Original-Call abfangen und durch Two-Call-Architektur ersetzen.
        return handlePredictiveSearch(url, input, options);
      }
    } catch (e) {
      console.warn('[SUPERPATRONEN Search] fetch-intercept failed, passing through:', e);
    }
    return originalFetch.call(this, input, options);
  };

  /**
   * Macht zwei parallele /search/suggest Calls (Drucker + Produkte) und
   * baut daraus eine synthetische Response zusammen, die wie das Original-
   * predictive-search.liquid-Markup aussieht.
   */
  function handlePredictiveSearch(originalUrl, originalInput, originalOptions) {
    const urlObj = new URL(originalUrl, window.location.origin);
    const originalQ = urlObj.searchParams.get('q') || '';
    const originalSection = urlObj.searchParams.get('section_id');

    // Wenn der Call schon auf eine unserer Sub-Sections zielt, durchreichen
    // (verhindert Endlosschleife, falls etwas die Sub-Sections direkt callt).
    if (originalSection === 'predictive-search-printers'
        || originalSection === 'predictive-search-products') {
      return originalFetch.call(window, originalInput, originalOptions);
    }

    // Vorigen Call abbrechen, falls noch in flight. Neuer Controller für
    // diesen Call. Das Signal wird beiden Sub-Calls mitgegeben.
    if (activePredictiveSearchController) {
      activePredictiveSearchController.abort();
    }
    const controller = new AbortController();
    activePredictiveSearchController = controller;

    // Basis-URL bauen: alle Original-Parameter übernehmen, q und section_id
    // werden pro Sub-Call überschrieben.
    function buildSubCallUrl(qValue, sectionId, resourceTypes) {
      const sub = new URL(urlObj.toString());
      sub.searchParams.set('q', qValue);
      sub.searchParams.set('section_id', sectionId);
      sub.searchParams.set('resources[type]', resourceTypes);
      return sub.toString();
    }

    const printerQ = normalize(originalQ, 'printer');
    const productQ = normalize(originalQ, 'cartridge');

    const printerUrl = buildSubCallUrl(
      printerQ,
      'predictive-search-printers',
      'query,collection,page,article'
    );
    const productUrl = buildSubCallUrl(
      productQ,
      'predictive-search-products',
      'product'
    );

    // Parallele Calls mit Abort-Signal. Promise.all wartet beide ab.
    const fetchOpts = { signal: controller.signal };
    const printerPromise = originalFetch.call(window, printerUrl, fetchOpts)
      .then(r => r.ok ? r.text() : '');
    const productPromise = originalFetch.call(window, productUrl, fetchOpts)
      .then(r => r.ok ? r.text() : '');

    return Promise.all([printerPromise, productPromise]).then(([printerHtml, productHtml]) => {
      // Doppelt prüfen: wurde der Call zwischenzeitlich abgebrochen?
      // Dann pending Promise zurückgeben, damit Datei 1 nicht mit stale
      // Daten rendert. Der nachfolgende Call übernimmt.
      if (controller.signal.aborted) {
        return new Promise(() => {});
      }
      const synthetic = buildSyntheticResponse(printerHtml, productHtml, originalQ);
      return new Response(synthetic, {
        status: 200,
        headers: { 'Content-Type': 'text/html; charset=utf-8' }
      });
    }).catch(err => {
      // AbortError ist erwartet, wenn ein neuer Call kommt → pending Promise,
      // damit kein .catch() in Datei 1 das Dropdown schließt.
      if (err && err.name === 'AbortError') {
        return new Promise(() => {});
      }
      console.warn('[SUPERPATRONEN Search] sub-call failed, falling back to single call:', err);
      // Fallback: Original-Call mit cartridge-Normalisierung durchführen.
      urlObj.searchParams.set('q', productQ);
      return originalFetch.call(window, urlObj.toString(), originalOptions);
    });
  }

  /**
   * Konstruiert ein synthetisches HTML-Dokument, das die gleiche Struktur
   * hat wie das Original predictive-search.liquid (damit Datei 1 ohne
   * Änderung weiterläuft).
   */
  function buildSyntheticResponse(printerHtml, productHtml, originalTerm) {
    // Sub-Section-Inhalt extrahieren und LIs zählen — in einem Parse pro Call.
    const printerData = extractSectionData(printerHtml, 'predictive-search-printers');
    const productData = extractSectionData(productHtml, 'predictive-search-products');

    const printerGroup = printerData.content;
    const productGroup = productData.content;
    const totalCount = printerData.count + productData.count;

    const hasPrinter = printerGroup.trim().length > 0;
    const hasProduct = productGroup.trim().length > 0;
    const twoColumnClass = (hasPrinter && hasProduct)
      ? 'predictive-search-results-group-wrapper-two-column'
      : '';
    const emptyClass = (totalCount === 0)
      ? 'predictive-search-results-list-empty'
      : '';

    // aria-labelledby dynamisch setzen: zeigt nur auf Headings, die
    // tatsächlich im DOM gerendert werden. Bei leerem Ergebnis ganz weglassen.
    // Vermeidet kaputte ARIA-Referenzen für Screen-Reader.
    const headingIds = [];
    if (hasPrinter) headingIds.push('predictive-search-printers-heading');
    if (hasProduct) headingIds.push('predictive-search-products-heading');
    const ariaLabelledByAttr = headingIds.length > 0
      ? `aria-labelledby="${headingIds.join(' ')}"`
      : '';

    // Term escapen für den "Suche nach"-Button.
    const safeTerm = escapeHtml(originalTerm || '');

    // Synthetisches Markup: identische Struktur zum Original-Liquid.
    return `<!DOCTYPE html><html><body><div id="shopify-section-predictive-search" class="shopify-section">
<div id="predictive-search-results">
  <div
    id="predictive-search-results-list"
    class="predictive-search-results-list ${emptyClass}"
    role="listbox"
    ${ariaLabelledByAttr}
  >
    <span class="spinner-xs"></span>
    <div class="predictive-search-results-group-wrapper ${twoColumnClass}">
      ${printerGroup}
      ${productGroup}
    </div>
  </div>
  <div
    id="predictive-search-option-search-keywords"
    class="predictive-search-list-item-term"
    role="option"
  >
    <button
      class="predictive-search-item-button predictive-search-item-term typograph-body"
      tabindex="-1"
    >
      Suche nach &quot;${safeTerm}&quot;
      <span class="angle angle-right"><span class="angle-icon"></span></span>
    </button>
  </div>
  ${totalCount === 0 ? '<div class="predictive-search-loading-state" aria-hidden="true"><span class="spinner-xs"></span></div>' : ''}
</div>
<span class="hidden" data-predictive-search-live-region-count-value>${totalCount}</span>
</div></body></html>`;
  }

  /**
   * Extrahiert in EINEM Parse-Vorgang sowohl den Section-innerHTML als auch
   * die Anzahl der <li>-Elemente. Spart einen DOMParser-Aufruf gegenüber
   * getrennter Extraction und Zählung.
   */
  function extractSectionData(html, sectionId) {
    if (!html) return { content: '', count: 0 };
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector(`#shopify-section-${sectionId}`);
      if (!section) return { content: '', count: 0 };
      return {
        content: section.innerHTML,
        count: section.querySelectorAll('li').length
      };
    } catch (e) {
      return { content: '', count: 0 };
    }
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ═══════════════════════════════════════════════════════════════════
  // INTEGRATION 2: Form-Submit (Result-Seite)
  // ═══════════════════════════════════════════════════════════════════
  // Beim Enter wird der Input-Wert kurz vor Submit auf die normalisierte
  // Variante gesetzt (mode='cartridge', weil Result-Seite Patronen-Produkte
  // zeigt).

  function attachFormHandlers(root) {
    const forms = (root || document).querySelectorAll('form[action*="/search"]');
    forms.forEach(form => {
      if (form.__superpatronenAttached) return;
      form.__superpatronenAttached = true;

      form.addEventListener('submit', function () {
        const input = form.querySelector('input[name="q"]');
        if (!input) return;
        const original = input.value.trim();
        if (!original) return;
        const normalized = normalize(original, 'cartridge');
        if (normalized !== original) {
          input.value = normalized;
        }
      });
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // INTEGRATION 3: Direct-Link-Redirect
  // ═══════════════════════════════════════════════════════════════════

  function handleDirectLink() {
    if (!window.location.pathname.includes('/search')) return;
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (!q) return;
    if (params.get('_sp') === '1') return;
    const normalized = normalize(q, 'cartridge');
    if (normalized !== q) {
      params.set('q', normalized);
      params.set('_sp', '1');
      const newUrl = window.location.pathname + '?' + params.toString() + window.location.hash;
      window.location.replace(newUrl);
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // BOOTSTRAP
  // ═══════════════════════════════════════════════════════════════════

  function init() {
    handleDirectLink();
    attachFormHandlers();
    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1) attachFormHandlers(node);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Debugging in der Browser-Konsole:
  //   SuperpatronenSearch.normalize('hp207x', 'cartridge')   → 'hp 207x'
  //   SuperpatronenSearch.normalize('hp207x', 'printer')     → 'hp 207 x'
  //   SuperpatronenSearch.normalize('m479dw', 'cartridge')   → 'm 479 dw'
  //   SuperpatronenSearch.normalize('m479dw', 'printer')     → 'm 479 dw'
  //   SuperpatronenSearch.normalize('m404x', 'cartridge')    → 'm 404x'
  //   SuperpatronenSearch.normalize('m404x', 'printer')      → 'm 404 x'
  //   SuperpatronenSearch.normalize('epson expression')      → 'epson expression'  (unverändert)
  window.SuperpatronenSearch = { normalize };
})();
